import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { articlesApi } from 'api/client';
import { SITE_NAME } from 'brand';
import Container from 'components/common/Container/Container';
import SEO from 'components/common/SEO/SEO';
import Interactions from 'components/Interactions';
import styles from './Articles.module.css';

export default function ArticleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [userVote, setUserVote] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const { article: data, userVote: uv } = await articlesApi.get(id);
        setArticle(data);
        setUserVote(uv || 0);
        document.title = `${data.title} · ${SITE_NAME}`;
      } catch (err) {
        if (err.status === 404) {
          navigate('/not-found', { replace: true });
        } else {
          setError('Failed to load the article.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id, navigate]);

  if (loading) return <Container className={styles.detailContainer} role="status">Loading article...</Container>;
  if (error) return <Container className={styles.detailContainer} style={{ color: 'var(--error)' }}>{error}</Container>;
  if (!article) return null;

  return (
    <Container className={styles.detailContainer} style={{ display: 'block' }}>
      <SEO 
        title={`${article.title} | ${SITE_NAME}`}
        description={`Read "${article.title}" by ${article.author} on AllCanAccess.`}
        ogImage={article.cover_image || '/og-image.png'}
        ogType="article"
      />
      <Link to="/articles" className={styles.backBtn}>
        <span aria-hidden="true">← </span>Back to Articles
      </Link>

      {article.cover_image && (
        <img src={article.cover_image} alt={article.cover_image_alt || ""} className={styles.detailImage} />
      )}

      <header className={styles.detailHeader}>
        <h1 className={styles.detailTitle}>{article.title}</h1>
        <div className={styles.detailMeta}>
          <span className={styles.author}>By {article.author}</span>
          <span className={styles.date}>{new Date(article.published_date).toLocaleDateString()}</span>
        </div>
      </header>

      {/* Renders the HTML generated by React Quill */}
      <div 
        className={styles.contentHtml} 
        dangerouslySetInnerHTML={{ __html: article.content_html }} 
      />

      <Interactions 
        id={article.id} 
        type="article" 
        api={articlesApi} 
        initialVotes={article.votes || 0} 
        initialUserVote={userVote} 
      />
    </Container>
  );
}

